import React, { useState, useEffect } from "react";
import {
  getAllUsers,
  approveUser,
  rejectUser,
  approvedUsers,
  rejectedUsers,
  pendingUsers,
  sendEmail,
} from "../Services/AllServices";
import "../styles/ModeratorUserManagement.css";
import RejectPopup from "./RejectUserPopUp";
import IDCardPopup from "./IDCardPopup";
import SortByAndStatusSelectDropdowns from "./SortByAndStatusSelectDropdowns";
import AcceptUserPopup from "./AcceptUserPopup";
import rejected from "../images/rejected.png";
import approved from "../images/approved.png";
import checkbox from "../images/checkbox.png";
import user_profile_photo from "../images/user_icon.png";
import call_icon from "../images/call_icon.png";
import mail_icon from "../images/mail_icon.png";
import fullscreenIcon from "../images/full_screen.png";

const ModeratorUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [openRejectPopup, setOpenRejectPopup] = useState(false);
  const [userIdToReject, setUserIdToReject] = useState(null);
  const [openApprovePopup, setOpenApprovePopup] = useState(false);
  const [userIdToApprove, setUserIdToApprove] = useState(null);
  const [userProfileToApprove, setUserProfileToApprove] = useState(null);
  const [licenseKey, setLicenseKey] = useState("");
  const [openIDCardPopup, setOpenIDCardPopup] = useState(false);
  const [idCardUrl, setIdCardUrl] = useState("");

  // Fetch all users when the component mounts
  useEffect(() => {
    fetchAllUsers(); // Fetch all users initially
  }, []);

  const fetchAllUsers = () => {
    getAllUsers((response) => {
      console.log("API Response:", response);
      if (Array.isArray(response.data)) {
        const sortedUsers = response.data.sort((a, b) => {
          // Use localeCompare for string comparison
          return b._id.localeCompare(a._id); // Adjust this based on your sorting criteria
        });
        setUsers(sortedUsers); // Set sorted videos
      } else {
        console.error("Data is not an array:", response);
      }
    });
  };

  const handleStatusSelect = (selectedStatus) => {
    switch (selectedStatus) {
      case "All":
        fetchAllUsers(); // Reset to show all users
        break;
      case "Approved":
        approvedUsers((response) => {
          if (Array.isArray(response.data)) {
            const sortedUsers = response.data.sort((a, b) => b.id - a.id);
            setUsers(sortedUsers);
          } else {
            console.error("Data is not an array:", response);
          }
        });
        break;
      case "Pending":
        pendingUsers((response) => {
          if (Array.isArray(response.data)) {
            const sortedUsers = response.data.sort((a, b) => b.id - a.id);
            setUsers(sortedUsers);
          } else {
            console.error("Data is not an array:", response);
          }
        });
        break;
      case "Rejected":
        rejectedUsers((response) => {
          if (Array.isArray(response.data)) {
            const sortedUsers = response.data.sort((a, b) => b.id - a.id);
            setUsers(sortedUsers);
          } else {
            console.error("Data is not an array:", response);
          }
        });
        break;
      default:
        break;
    }
  };
  const handleSort = (order) => {
    let sortedUsers;
    if (order === "Latest") {
      sortedUsers = [...users].sort((a, b) => b._id.localeCompare(a._id)); // Sort for latest
    } else if (order === "Oldest") {
      sortedUsers = [...users].sort((a, b) => a._id.localeCompare(b._id)); // Sort for oldest
    }
    setUsers(sortedUsers);
  };

  const handleApproveButtonClick = (userId, profile) => {
    setUserIdToApprove(userId);
    setUserProfileToApprove(profile);
    setOpenApprovePopup(true);
  };
  const handleRejectButtonClick = (userId) => {
    setUserIdToReject(userId);
    setOpenRejectPopup(true); // Open the reject popup
  };
  // Inside ModeratorUserManagement.js

  const handleApprove = async (startDate, endDate) => {
    const user = users.find((user) => user._id === userIdToApprove);

    // Log user details for debugging
    console.log("Approving user:", {
      name: user?.name,
      role: user?.profile,
      email: user?.email,
    });

    if (!user) {
      console.error("User not found.");
      return;
    }

    try {
      setOpenApprovePopup(false); // Close popup

      if (user.profile === "Teacher") {
        if (!startDate || !endDate || !licenseKey) {
          console.error("Missing required fields for teacher approval.");
          return;
        }
        // Approve teacher
        const formData = {
          user_id: userIdToApprove,
          licenseKey,
          start_date: startDate,
          end_date: endDate,
        };

        await approveUser(formData, (response) => {
          console.log("Teacher approved:", response);
          setUsers(
            users.map((u) =>
              u._id === userIdToApprove ? { ...u, status: "approved" } : u
            )
          );
        });
      } else if (user.profile === "Student") {
        // Approve student without license key and dates
        const formData = { user_id: userIdToApprove };

        await approveUser(formData, (response) => {
          console.log("Student approved:", response);
          setUsers(
            users.map((u) =>
              u._id === userIdToApprove ? { ...u, status: "approved" } : u
            )
          );
        });
      }

      // Send email
      const emailSubject = "Acceptance of your profile at AnaVu";
      const emailBody = `Hello ${user.name},\n\nWe are pleased to inform you that your profile has been accepted.`;
      await sendEmail(user.email, emailSubject, emailBody, (emailResponse) =>
        console.log("Email Response:", emailResponse)
      );
    } catch (error) {
      console.error("Error approving user:", error);
    } finally {
      // Reset state
      setUserIdToApprove(null);
      setUserProfileToApprove(null);
      setLicenseKey("");
    }
  };

  const handleReject = async () => {
    if (userIdToReject) {
      const formData = { user_id: userIdToReject };

      try {
        // Close the popup first
        setOpenRejectPopup(false); // Close the reject popup

        await rejectUser(formData, (response) => {
          // Update user status to 'rejected'
          setUsers(
            users.map((user) =>
              user._id === userIdToReject
                ? { ...user, status: "rejected" }
                : user
            )
          );
        });

        // Send rejection email
        const user = users.find((user) => user._id === userIdToReject);
        const emailSubject = "Rejection of your profile at AnaVu";
        const emailBody = `Hello ${user.name},\n\nWe regret to inform you that your profile has been rejected. If you have any questions or would like to know more, please contact our support team at productlab.tto@gmail.com.\n\nThank you for your understanding.\n\nBest regards,\nThe AnaVu Team`;

        // Use setTimeout to send email after popup closes
        setTimeout(async () => {
          await sendEmail(
            user.email,
            emailSubject,
            emailBody,
            (emailResponse) => {
              console.log("Email Response:", emailResponse);
            }
          );
        }, 0); // Schedule the email sending to run immediately after the current call stack
      } catch (error) {
        console.error("Error rejecting user:", error);
      } finally {
        setUserIdToReject(null); // Reset the userId
      }
    }
  };

  // Function to open ID card popup
  const handleIDCardClick = (idCardUrl) => {
    setIdCardUrl(idCardUrl); // Set the ID card URL from user object
    setOpenIDCardPopup(true); // Open the popup
  };

  return (
    <div className="user-management">
      <div className="moderator-users-container">
        <h1>User Management</h1>

        {/* Dropdowns for filtering and sorting */}
        <SortByAndStatusSelectDropdowns
          onStatusChange={handleStatusSelect}
          onSortByChange={handleSort} // Pass the sorting function here
        />

        <h2>Users</h2>

        <div className="users-list">
          {Array.isArray(users) && users.length > 0 ? (
            users.map((user) => (
              <div
                key={user._id}
                className={`user-card ${user.status?.toLowerCase()}`}
              >
                <div className="user-list-left">
                  <div className="moderator-user-icon">
                    <img src={user_profile_photo} alt="Profile" />
                  </div>
                </div>
                <div className="user_details">
                  <div className="user_name_role">
                    <div className="user_name">{user.name}</div>
                    <div className="user_role">
                      {" "}
                      {user.profile} {user.role && `- ${user.role}`}{" "}
                    </div>
                  </div>
                  <div className="user_purpose_contact">
                    <div className="user-purposes">
                      {Array.isArray(user.purpose) && user.purpose.length > 0
                        ? user.purpose.map((purpose, index) => (
                            <div key={index} style={{ display: "flex" }}>
                              <div className="user_purpose">{purpose}</div>
                              <div className="user_checkbox">
                                <img src={checkbox} alt="Checkbox" />
                              </div>
                            </div>
                          ))
                        : null}
                    </div>
                    <div className="user_call_container">
                      <div className="user_call_icon">
                        <img src={call_icon} alt="call_icon" />
                      </div>
                      <div className="user_contact_text">
                        <span>-</span>
                        <span className="user_phoneNumber">
                          {user.mobileNumber}
                        </span>
                      </div>
                    </div>
                    <div className="user_mail_container">
                      <div className="user_mail_icon">
                        <img src={mail_icon} alt="mail_icon" />
                      </div>
                      <div className="user_contact_text">
                        <span>-</span>
                        <span className="user_email">{user.email}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="teacher_id_card">
                  {user.status.toLowerCase() === "pending" &&
                    user.profile === "Teacher" && (
                      <>
                        <div
                          className="id_card_img"
                          onClick={() => handleIDCardClick(user.ID_card.url)}
                        >
                          <img src={user.ID_card.url} alt="id card" />
                        </div>
                        <div className="fullscreen-icon-container">
                          <img src={fullscreenIcon} alt="Full Screen" />
                        </div>
                      </>
                    )}
                </div>

                <div className="user_status_buttons">
                  {user.status.toLowerCase() === "pending" && (
                    <div className="user_actions">
                      <button
                        className="user_reject"
                        onClick={() => handleRejectButtonClick(user._id)}
                      >
                        Reject
                      </button>
                      <button
                        className="user_approve"
                        onClick={() =>
                          handleApproveButtonClick(user._id, user.profile)
                        }
                      >
                        Approve
                      </button>
                    </div>
                  )}

                  {user.status.toLowerCase() === "approved" && (
                    <div className="user_status_label">
                      <div className="user_approve_img">
                        <img src={approved} alt="approve" />
                      </div>
                      <div>Approved</div>
                    </div>
                  )}

                  {user.status.toLowerCase() === "rejected" && (
                    <div className="user_status_label">
                      <div className="user_reject_img">
                        <img src={rejected} alt="reject" />
                      </div>
                      <div>Rejected</div>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p>No users found.</p>
          )}
        </div>

        <IDCardPopup
          open={openIDCardPopup}
          idCardUrl={idCardUrl}
          onClose={() => setOpenIDCardPopup(false)}
        />

        <RejectPopup
          open={openRejectPopup}
          onClose={() => setOpenRejectPopup(false)} // Close popup
          onReject={handleReject} // Call handleReject on confirm
        />
        <AcceptUserPopup
          open={openApprovePopup}
          userProfile={userProfileToApprove} // Pass the role to the popup
          licenseKey={licenseKey}
          onLicenseKeyChange={setLicenseKey} // Function to update license key
          onClose={() => setOpenApprovePopup(false)}
          onApprove={handleApprove}
        />
      </div>
    </div>
  );
};

export default ModeratorUserManagement;
